import { create } from 'zustand';

import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@/shared/constants/pagination';

import type {
  PatientInquirySortKey,
  SortDirection,
} from '../types/patient-inquiry.types';

export interface PatientInquiryListFilters {
  // Free-text — case-insensitive partial match on name / phone / title (PRD §3.2).
  search: string;
}

export interface PatientInquiryListSort {
  key: PatientInquirySortKey;
  dir: SortDirection;
}

// PRD §2.2 #3 — rows-per-page dropdown. SHCT-715: the prototype shipped a single
// "20" option here; the admin now offers one shared 20/30/50 set on every list.
export const PATIENT_INQUIRY_PER_PAGE_OPTIONS = PAGE_SIZE_OPTIONS;

// Clarification ADM.035 (2026-07-14): support asc/desc sorting ONLY for
// Inquiry Date & Time and Inquiry Status. Other columns are not sortable.
export const PATIENT_INQUIRY_SORTABLE_KEYS: ReadonlySet<PatientInquirySortKey> =
  new Set<PatientInquirySortKey>(['inquiredAt', 'status']);

interface PatientInquiryListState {
  filters: PatientInquiryListFilters;
  sort: PatientInquiryListSort;
  // False when sort is the default (inquiredAt desc); drives the column-header
  // indicator (↕ on non-default columns, ↓ on inquiredAt at rest).
  userSorted: boolean;
  page: number;
  perPage: number;
  setSearch: (search: string) => void;
  // PRD §4.1 3-click cycle: asc → desc → cleared (back to default).
  toggleSort: (key: PatientInquirySortKey) => void;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  reset: () => void;
}

const INITIAL_FILTERS: PatientInquiryListFilters = { search: '' };
// PRD §4.1 / §3.1: default sort is 문의일시(inquiredAt) descending (newest first).
const INITIAL_SORT: PatientInquiryListSort = { key: 'inquiredAt', dir: 'desc' };

export const usePatientInquiryListStore = create<PatientInquiryListState>((set) => ({
  filters: INITIAL_FILTERS,
  sort: INITIAL_SORT,
  userSorted: false,
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
  // Search / sort / perPage changes reset pagination to page 1 (prototype behavior).
  setSearch: (search) => set({ filters: { search }, page: 1 }),
  // ADM.035: only inquiredAt / status are sortable — ignore any other key.
  toggleSort: (key) => {
    if (!PATIENT_INQUIRY_SORTABLE_KEYS.has(key)) return;
    set((s) => {
      if (s.userSorted && s.sort.key === key) {
        if (s.sort.dir === 'asc') {
          return { sort: { key, dir: 'desc' }, userSorted: true, page: 1 };
        }
        return { sort: INITIAL_SORT, userSorted: false, page: 1 };
      }
      return { sort: { key, dir: 'asc' }, userSorted: true, page: 1 };
    });
  },
  setPage: (page) => set({ page }),
  setPerPage: (perPage) => set({ perPage, page: 1 }),
  reset: () =>
    set({
      filters: INITIAL_FILTERS,
      sort: INITIAL_SORT,
      userSorted: false,
      page: 1,
      perPage: DEFAULT_PAGE_SIZE,
    }),
}));
